import React, { useContext, useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowBack, Dashboard as DashboardIcon } from '@mui/icons-material';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import {
  DashboardList,
  DashboardOption,
  externalDashboardsApi,
  DashboardListResponse,
} from 'modules/integrations/externalDashboards';
import { OPTION_LIST_ICON_MAPPER, TRNSACT_DASHBOARD_TYPE } from 'modules/integrations/externalDashboards/constants';
import { LenderActivityDashboard } from 'modules/lenderActivityDashboard';

import { accountSelectors } from '../../redux/accountReducer';
import { externalDashboardPortalDefaultHiddenFor } from '../../modules/portalConfigurations/externalDashboardPortal/constants';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { UserProfile } from 'types';

interface LocationState {
  resetView?: boolean;
  openDashboardId?: string;
}

export const ExternalDashboards = () => {
  const accountType = useSelector(accountSelectors.accountType);
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardOption | null>(null);
  const location = useLocation<LocationState>();

  const { isSuperAdminOrSingleAccount } = useContext(CommonDataContext);

  const hasAutoOpenedRef = useRef(false);

  // Reset selected dashboard when navigating back to /dashboard route
  useEffect(() => {
    if (location.pathname === '/dashboard' && location.state?.resetView) {
      setSelectedDashboard(null);
    }
  }, [location]);

  const { data, loading, error } = useQuery<DashboardListResponse>(externalDashboardsApi.getDashboardList, {
    notifyOnNetworkStatusChange: true,
    skip: !accountType,
    variables: {
      input: { accountType },
    },
  });

  const externalDashboards = data?.externalDashboards?.result?.list ?? [];

  const userProfile = useSelector((state: any) => state.userProfile) as UserProfile;
  const portalConfigExternalDashboards = useSelector<any, Record<string, any>>(state =>
    portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.ExternalDashboardAccess)
  );

  const accessConfig: Record<string, string[]> =
    portalConfigExternalDashboards?.jsonDefinition?.config?.dashboardHiddenFor ?? {};

  const dashboardOptionList: DashboardOption[] = externalDashboards
    .map(listItem => {
      const hiddenForRoles = accessConfig[listItem.id] ?? externalDashboardPortalDefaultHiddenFor;
      const isHasAccess = !hiddenForRoles.includes(userProfile.vendorContactRole);

      return {
        ...listItem,
        isVisible: isSuperAdminOrSingleAccount || isHasAccess,
      };
    })
    .filter(({ isVisible = false }) => isVisible);

  // Auto-open the dashboard requested via the post-login landing redirect (once).
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;

    const openDashboardId = location.state?.openDashboardId;
    if (!openDashboardId) return;

    const target = dashboardOptionList.find(({ id }) => id === openDashboardId);
    if (target) {
      setSelectedDashboard(target);
      hasAutoOpenedRef.current = true;
    }
  }, [location.state, dashboardOptionList]);

  const handleDashboardSelect = (dashboard: DashboardOption) => {
    setSelectedDashboard(dashboard);
  };

  const handleBack = () => {
    setSelectedDashboard(null);
  };

  // If a dashboard is selected, show full-screen viewer
  if (selectedDashboard) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          flex: 1,
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '0.75rem 1rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Tooltip title="Back to dashboards">
            <IconButton size="small" onClick={handleBack}>
              <ArrowBack />
            </IconButton>
          </Tooltip>

          {selectedDashboard.icon ? OPTION_LIST_ICON_MAPPER[selectedDashboard.icon] : <DashboardIcon color="primary" />}

          <Typography variant="h6" component="h1" color="textSecondary">
            {selectedDashboard.name}
          </Typography>
        </Box>

        {/* Dashboard iframe */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: selectedDashboard.type === TRNSACT_DASHBOARD_TYPE ? 'auto' : 'hidden',
          }}
        >
          {selectedDashboard.type === TRNSACT_DASHBOARD_TYPE ? (
            <Box sx={{ p: 2 }}>
              <LenderActivityDashboard />
            </Box>
          ) : (
            <iframe
              src={selectedDashboard.url}
              title={selectedDashboard.name}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
              allowTransparency
            />
          )}
        </Box>
      </Box>
    );
  }

  // Show dashboard list
  return (
    <PageContainer title="External Dashboards" loading={loading} error={error ?? null}>
      <DashboardList dashboardOptionList={dashboardOptionList} onDashboardSelect={handleDashboardSelect} />
    </PageContainer>
  );
};
